/**
 * Filter Components module
 * Object-oriented approach to filter component management using Adapter Pattern
 */

import { CONFIG } from '../../core/config.js';
import { AdapterFactory, WidgetTypeDetector } from './adapters/adapter-factory.js';

/**
 * Base class for all filter components
 * Uses Adapter Pattern to work with different widget types through a unified interface
 */
export class FilterComponent {
    constructor(element, adapter = null) {
        this.element = element;
        this.adapter = adapter;
        this.id = element?.id;
        this.minTextLength = CONFIG.FILTER.TEXT_INPUT.MIN_LENGTH;
        this.debounceDelay = CONFIG.FILTER.TEXT_INPUT.DEBOUNCE_DELAY;
        this.isInitialized = false;
    }

    /**
     * Initialize auto-filtering for this component
     * Uses the adapter to bind events appropriately
     */
    initializeAutoFilter(onFilter) {
        if (!this.adapter) {
            // eslint-disable-next-line no-console
            console.warn(`FilterComponent ${this.id}: No adapter available`);
            return;
        }

        try {
            // Check if this is a text input that needs debouncing
            if (WidgetTypeDetector.isTextInput(this.element)) {
                this._initializeTextInputAutoFilter(onFilter);
            } else {
                // For Kendo widgets, use the adapter's bind method
                this.adapter.bind('change', onFilter);
            }

            this.isInitialized = true;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`FilterComponent ${this.id}: Failed to initialize auto-filter:`, error);
            throw error;
        }
    }

    /**
     * Initialize auto-filtering for text inputs with debouncing
     */
    _initializeTextInputAutoFilter(onFilter) {
        if (!this.element) return;

        const input = $(this.element);
        let debounceTimer;
        let lastFilteredValue = '';

        const shouldFilter = value => {
            return value.length === 0 || value.length >= this.minTextLength;
        };

        // On input change (while typing)
        input.on('input', () => {
            const value = input.val();
            clearTimeout(debounceTimer);

            if (shouldFilter(value)) {
                debounceTimer = setTimeout(() => {
                    if (value !== lastFilteredValue) {
                        lastFilteredValue = value;
                        onFilter();
                    }
                }, this.debounceDelay);
            }
        });

        // On blur (when user leaves input)
        input.on('blur', () => {
            const value = input.val();
            clearTimeout(debounceTimer);

            // Trigger filtering even if value is shorter than 3 characters, if value changed
            if (value !== lastFilteredValue) {
                lastFilteredValue = value;
                onFilter();
            }
        });

        // On Enter key press
        input.on('keypress', e => {
            if (e.which === 13) {
                // Enter key
                const value = input.val();
                clearTimeout(debounceTimer);

                if (shouldFilter(value) && value !== lastFilteredValue) {
                    lastFilteredValue = value;
                    onFilter();
                }
            }
        });
    }

    /**
     * Check if this component has an active filter value
     * Delegates to the adapter
     */
    hasValue() {
        return this.adapter ? this.adapter.hasValue() : false;
    }

    /**
     * Clear the component's value
     * Delegates to the adapter
     */
    clearValue() {
        return this.adapter ? this.adapter.clearValue() : false;
    }

    /**
     * Get the component's current value
     * Delegates to the adapter
     */
    getValue() {
        return this.adapter ? this.adapter.getValue() : null;
    }

    /**
     * Set the component's value
     * Delegates to the adapter
     */
    setValue(value) {
        return this.adapter ? this.adapter.setValue(value) : false;
    }

    /**
     * Check if this is a compound component (e.g., DateRangePicker producing multiple filter entries)
     */
    get isCompound() {
        return this.adapter?.isCompound === true;
    }

    /**
     * Get individual filter entries for compound components (e.g., DateRangePicker).
     * Returns array of [fieldName, value] pairs.
     * Delegates to the adapter.
     * @returns {Array<[string, *]>}
     */
    getFilterEntries() {
        if (this.adapter && typeof this.adapter.getFilterEntries === 'function') {
            return this.adapter.getFilterEntries();
        }
        return [];
    }

    /**
     * Check if the component is ready (adapter is available and widget is initialized)
     */
    isReady() {
        return this.adapter && this.adapter.isAvailable();
    }

    /**
     * Update the adapter when the underlying widget becomes available
     */
    updateAdapter() {
        if (this.adapter && AdapterFactory.isAdapterPending(this.adapter)) {
            return AdapterFactory.updatePendingAdapter(this.adapter);
        }
        return false;
    }
}

/**
 * Factory for creating filter components
 * Uses Adapter Pattern to create unified FilterComponent instances
 */
export class FilterComponentFactory {
    /**
     * Create a filter component for the given element
     * @param {HTMLElement} element The DOM element
     * @returns {FilterComponent} Filter component instance
     */
    static createComponent(element) {
        if (!element || !element.id) {
            throw new Error('Element must have an id');
        }

        try {
            // Create appropriate adapter for the element
            const adapter = AdapterFactory.createAdapter(element);

            // Create filter component with the adapter
            return new FilterComponent(element, adapter);
        } catch (error) {
            // If adapter creation fails (e.g., for Kendo component inputs), skip this element
            // eslint-disable-next-line no-console
            console.warn(`FilterComponentFactory: Skipping element ${element.id}:`, error.message);
            return null;
        }
    }

    /**
     * Create multiple filter components for an array of elements
     * @param {HTMLElement[]} elements Array of DOM elements
     * @returns {FilterComponent[]} Array of filter component instances (nulls filtered out)
     */
    static createComponents(elements) {
        return elements
            .map(element => this.createComponent(element))
            .filter(component => component !== null);
    }

    /**
     * Update pending adapters for components that are waiting for Kendo widgets
     * @param {FilterComponent[]} components Array of filter components
     * @returns {number} Number of components that were updated
     */
    static updatePendingComponents(components) {
        let updatedCount = 0;

        components.forEach(component => {
            if (component.updateAdapter()) {
                updatedCount++;
            }
        });

        return updatedCount;
    }
}
